/**
 * Mise en forme — aucun calcul métier. Les chaînes décimales de l'API
 * sont converties en `number` uniquement pour l'affichage.
 */
const nf = new Intl.NumberFormat('fr-FR')

/** Le franc CFA (XOF) n'a pas de sous-unité : on affiche l'entier. */
export const money = (v: string | null | undefined): string =>
  v == null ? '—' : `${nf.format(Math.round(Number(v)))} FCFA`

export const moneyShort = (v: string | null | undefined): string => {
  if (v == null) return '—'
  const n = Number(v)
  const a = Math.abs(n)
  if (a >= 1e9) return `${(n / 1e9).toFixed(1).replace('.', ',')} Md`
  if (a >= 1e6) return `${(n / 1e6).toFixed(1).replace('.', ',')} M`
  if (a >= 1e3) return `${Math.round(n / 1e3)} k`
  return String(Math.round(n))
}

export const liters = (v: string | null | undefined): string =>
  v == null ? '—' : `${nf.format(Math.round(Number(v)))} L`

/** Trois décimales conservées : c'est la précision d'un index de pompe. */
export const litersExact = (v: string | null | undefined): string =>
  v == null
    ? '—'
    : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} L`

export const meterIndex = (v: string | null | undefined): string =>
  v == null
    ? '—'
    : Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })

export const millimeters = (v: string | null | undefined): string =>
  v == null ? '—' : `${nf.format(Math.round(Number(v)))} mm`

export const signed = (v: string | null | undefined): string => {
  if (v == null) return '—'
  const n = Math.round(Number(v))
  return `${n > 0 ? '+' : ''}${nf.format(n)}`
}

export const count = (n: number): string => nf.format(n)

export const percentLabel = (n: number): string =>
  `${n > 0 ? '+' : ''}${String(n).replace('.', ',')} %`

/* ── Dates ───────────────────────────────────────────────────── */

const dateFmt = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})
const timeFmt = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' })
const dateTimeFmt = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})

export const date = (iso: string | null | undefined): string =>
  iso ? dateFmt.format(new Date(iso)) : '—'
export const time = (iso: string | null | undefined): string =>
  iso ? timeFmt.format(new Date(iso)) : '—'
export const dateTime = (iso: string | null | undefined): string =>
  iso ? dateTimeFmt.format(new Date(iso)) : '—'

/** « il y a 12 min », « il y a 3 h », « hier »… */
export const relative = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.round(diff / 60_000)
  if (min < 1) return 'à l’instant'
  if (min < 60) return `il y a ${min} min`
  const h = Math.round(min / 60)
  if (h < 24) return `il y a ${h} h`
  const d = Math.round(h / 24)
  if (d === 1) return 'hier'
  if (d < 30) return `il y a ${d} j.`
  return dateFmt.format(new Date(iso))
}

/** Jours entre `iso` et maintenant (positif si passé). */
export const daysSince = (iso: string | null | undefined): number =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000) : 0

export const initials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')

/** Date-heure locale au format `YYYY-MM-DDTHH:mm` pour un `<input type="datetime-local">`. */
export const nowLocal = (): string => {
  const d = new Date()
  d.setSeconds(0, 0)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export const todayLocal = (): string => nowLocal().slice(0, 10)

/** `datetime-local` → ISO 8601 avec fuseau, ce que le back attend. */
export const toIso = (local: string): string => new Date(local).toISOString()

/* ── Libellés ────────────────────────────────────────────────── */

/**
 * Libellés des rôles livrés. Un rôle composé par le superadmin n'y figure
 * pas : son nom technique est alors affiché tel quel.
 */
export const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Superadmin',
  director: 'Directeur général',
  manager: 'Gérant de station',
  controller: 'Contrôleur',
  accountant: 'Comptable',
  pump_attendant: 'Pompiste',
}

/** Domaines de permissions, déduits du préfixe `domaine.action`. */
export const PERMISSION_GROUP_LABELS: Record<string, string> = {
  roles: 'Rôles et permissions',
  modules: 'Modules',
  users: 'Comptes',
  'audit-logs': 'Journal d’audit',
  network: 'Périmètre réseau',
  stations: 'Stations',
  inventory: 'Stock',
  'tank-gaugings': 'Jaugeages',
  'work-shifts': 'Postes et caisse',
  procurement: 'Approvisionnement',
  credit: 'Clients à crédit',
  anomalies: 'Anomalies',
  alerts: 'Alertes',
  maintenance: 'Maintenance',
  inspections: 'Contrôles terrain',
}

export const SHIFT_STATUS: Record<
  string,
  { label: string; tone: 'good' | 'warn' | 'bad' | 'info' }
> = {
  open: { label: 'Ouvert', tone: 'info' },
  pending_validation: { label: 'À valider', tone: 'warn' },
  discrepancy: { label: 'Écart', tone: 'bad' },
  validated: { label: 'Validé', tone: 'good' },
}

export const MODULE_LABELS: Record<string, string> = {
  inventory: 'Stock & cuves',
  operations: 'Relevés & caisse',
  procurement: 'Approvisionnement',
  credit: 'Clients à crédit',
  risk: 'Surveillance',
  maintenance: 'Maintenance',
  inspections: 'Contrôles terrain',
  vouchers: 'Bons carburant',
  shop: 'Boutique',
  reports: 'Rapports',
  integrations: 'Intégrations',
  loyalty: 'Fidélité',
  financing: 'Financement',
}
